/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  AppAlert,
  BlockUI,
  IStudyOrderDetailsResponse,
  PortalCard,
  ReportCard,
  calculateRemainingMinutes,
} from "@advapacs/common";
import { IRootState } from "@src/config/reducers";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  NavLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAppDispatch } from "@src/config/store";
import { downloadFile, getAllSeries, getStudyDetail } from "./ducks/operators";
import { Tab, Tabs } from "react-bootstrap";
import ImageIcon from "@src/assets/icons/imagesmode-bl.svg";
import ReportSidebarIcon from "@src/assets/icons/rp-sb-icon.svg";
import { ImageViewer } from "@src/components/ImageViewer";
import { ReportViewer } from "./ReportViewer";
import { ExamActions } from "./ducks/slices";
import { instanceAPI } from "@src/config/api";

enum TabKey {
  REPORT = "REPORT",
  IMAGES = "IMAGES",
}

export interface ImagesViewerModalProps {
  src: string;
  name: string;
  id: string;
  dataSrc?: string;
}

/**
 * /Exams/:id
 * @returns React.ReactNode
 */

const ExamDetailsContainer: FC = () => {
  const { studyId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const { pathname } = useLocation();
  const [key, setKey] = useState<TabKey>();

  useEffect(() => {
    if (type === "images") setKey(TabKey.IMAGES);
    else setKey(TabKey.REPORT);
  }, [type]);

  const { studyOrOrderDetails, allSeries, loading } = useSelector(
    (state: IRootState) => state.examsReducer
  );
  const { isMobile, isMobileOnly } = useSelector(
    (state: IRootState) => state.responsiveReducer
  );
  const { account } = useSelector(
    (state: IRootState) => state.unAuthenticatedReducer
  );

  const [images, setImages] = useState<Array<ImagesViewerModalProps>>([]);

  useEffect(() => {
    if (studyId) {
      dispatch(getStudyDetail(studyId));
    }
    dispatch(getAllSeries(studyId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId]);

  useEffect(() => {
    if (
      studyOrOrderDetails?.id &&
      studyOrOrderDetails?.hasImages &&
      calculateRemainingMinutes(studyOrOrderDetails?.imagesAvailableAt) <= 0
    ) {
      const result = (allSeries || []).map((item) => {
        return {
          src: `/studies/${studyId}/series/${item.id}/thumbnail?quality=50`,
          name: item?.description,
          id: item.id,
        };
      });
      setImages(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyOrOrderDetails?.id, studyId, allSeries]);

  useEffect(() => {
    return () => {
      dispatch(ExamActions.clearData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disableReportTab = useMemo(() => {
    return (
      !studyOrOrderDetails?.hasReport ||
      calculateRemainingMinutes(studyOrOrderDetails?.reportAvailableAt) > 0 ||
      calculateRemainingMinutes(studyOrOrderDetails?.reportAvailableUntil) < 0
    );
  }, [studyOrOrderDetails]);

  const disableImageTab = useMemo(() => {
    return (
      !studyOrOrderDetails?.hasImages ||
      calculateRemainingMinutes(studyOrOrderDetails?.imagesAvailableAt) > 0 ||
      calculateRemainingMinutes(studyOrOrderDetails?.imagesAvailableUntil) < 0
    );
  }, [studyOrOrderDetails]);

  const downloadByUrl = (url: string) => {
    dispatch(downloadFile(url));
  };

  const getTabIconFilter = (tabKey: TabKey, isDisabled: boolean) => {
    return isDisabled || key !== tabKey
      ? "brightness(0) invert(50%)" // converts icon to gray: brightness(0) → black, invert(50%) → ~#808080
      : "none";
  };

  return (
    <BlockUI isBlocked={loading}>
      <p className="">
        <NavLink to={"/Exams"} className="text-primary text-decoration-none">
          <i className="fa fa-chevron-left"></i>
          View All Exams
        </NavLink>
      </p>
      <div className="exam-details">
        <PortalCard wrapperClass=" m-b-xs border-none b-r-none box-shadow-none">
          <ReportCard
            dateFormat={account?.localisation?.dateTimeFormat}
            plainDateFormat={account?.localisation?.dateFormat}
            isMobile={isMobile}
            isMobileOnly={isMobileOnly}
            item={studyOrOrderDetails as IStudyOrderDetailsResponse}
          />
        </PortalCard>

        <Tabs
          defaultActiveKey={TabKey.REPORT}
          className={`mb-tab-custom line-background ${
            isMobileOnly ? "mobile " : "desktop"
          }`}
          onSelect={(tk) => {
            setKey(tk as TabKey);

            navigate(
              `${pathname}?type=${tk === TabKey.IMAGES ? "images" : "report"}`
            );
          }}
          activeKey={key}
        >
          <Tab
            className={`${
              !studyOrOrderDetails?.hasReport ? "nav-item-disabled" : ""
            }`}
            disabled={disableReportTab}
            eventKey={TabKey.REPORT}
            title={
              <span
                style={{ opacity: disableReportTab ? 0.65 : "" }}
                className="d-flex flex-center"
                title={!studyOrOrderDetails?.hasReport ? "No Report" : ""}
              >
                <img
                  className="m-r-xxs"
                  src={ReportSidebarIcon}
                  style={{
                    filter: getTabIconFilter(TabKey.REPORT, disableReportTab),
                  }}
                />
                <span>Report</span>
              </span>
            }
          >
            <PortalCard wrapperClass="study-card h-100">
              <ReportViewer
                isMobile={isMobile}
                isLoadData={
                  studyOrOrderDetails?.hasReport &&
                  calculateRemainingMinutes(
                    studyOrOrderDetails?.reportAvailableAt
                  ) <= 0
                }
                viewModeId={studyId}
              />
            </PortalCard>
          </Tab>

          <Tab
            className={`${
              !studyOrOrderDetails?.hasImages ? "nav-item-disabled" : ""
            }`}
            disabled={disableImageTab}
            eventKey={TabKey.IMAGES}
            title={
              <span
                style={{ opacity: disableImageTab ? 0.65 : "" }}
                className="d-flex flex-center"
              >
                <img
                  className="m-r-xxs"
                  src={ImageIcon}
                  style={{
                    filter: getTabIconFilter(TabKey.IMAGES, disableImageTab),
                  }}
                />
                <span>Images</span>
              </span>
            }
          >
            <PortalCard wrapperClass="study-card h-100">
              <h5
                className={`font-weight m-b-${
                  studyOrOrderDetails?.anySeriesHidden ? "xs" : "md"
                }`}
              >
                Images ({studyOrOrderDetails?.imageCount})
              </h5>
              {studyOrOrderDetails?.anySeriesHidden && (
                <AppAlert.SimpleAlert
                  variant="info"
                  message={"Not all images are visible due to system rules"}
                />
              )}
              <ImageViewer
                downloadInstance={(stdId, sId, instanceId, frame) => {
                  dispatch(
                    downloadFile(
                      `${instanceAPI.GET_RENDER_INSTANCE(
                        stdId,
                        sId,
                        instanceId
                      )}?quality=100&frame=${frame}&download=true`
                    )
                  );
                }}
                downloadByUrl={downloadByUrl}
                downloadSeries={(stdId, seriesId) => {
                  dispatch(
                    downloadFile(
                      `${instanceAPI.DOWNLOAD_SERIES_AS_ZIP(
                        stdId,
                        seriesId
                      )}?quality=100&frame=1&download=true`
                    )
                  );
                }}
                studyId={studyId}
                images={images}
              />
            </PortalCard>
          </Tab>
        </Tabs>
      </div>
    </BlockUI>
  );
};

export default ExamDetailsContainer;
